import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuthState } from '@/hooks/useAuth'
import { AdminAuthGuard } from '@/components/admin/AdminAuthGuard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, Users, Heart, MessageSquare, FileText, TrendingUp, Palette, UserPlus, ChevronLeft } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { ANALYTICS_ENDPOINT } from '@/config/endpoints'
import type { AnalyticsResponse } from '@/types/models'

export default function AnalyticsPage() {
  const { user } = useAuthState()
  const [analyticsData, setAnalyticsData] = useState<AnalyticsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('30')

  useEffect(() => {
    if (user) {
      fetchAnalytics(parseInt(selectedTimeframe))
    }
  }, [selectedTimeframe, user])

  const fetchAnalytics = async (lastXDays: number) => {
    setIsLoading(true)
    setError(null)

    try {
      if (!user) {
        setError('Not authenticated')
        return
      }

      const idToken = await user.getIdToken()

      const response = await fetch(ANALYTICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ lastXDays })
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch analytics: ${response.statusText}`)
      }

      const data = await response.json()
      setAnalyticsData(data)
    } catch (err) {
      console.error('Error fetching analytics:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics')
    } finally {
      setIsLoading(false)
    }
  }

  const processSignupTimestamps = (timestamps: string[]) => {
    if (timestamps.length === 0) return []

    const dailySignups: { [key: string]: number } = {}
    let earliestDate: Date | null = null
    let latestDate: Date | null = null
    
    timestamps.forEach((timestamp) => {
      const date = new Date(timestamp)
      const dateKey = date.toISOString().split('T')[0]
      dailySignups[dateKey] = (dailySignups[dateKey] || 0) + 1

      if (!earliestDate || date < earliestDate) earliestDate = date
      if (!latestDate || date > latestDate) latestDate = date
    })

    if (!earliestDate || !latestDate) return []

    const allDates: Array<{ date: string; count: number; fullDate: string }> = []
    const currentDate = new Date(earliestDate)
    currentDate.setHours(0, 0, 0, 0)
    const latestDateNormalized = new Date(latestDate)
    latestDateNormalized.setHours(0, 0, 0, 0)

    while (currentDate <= latestDateNormalized) {
      const dateKey = currentDate.toISOString().split('T')[0]
      allDates.push({
        date: currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count: dailySignups[dateKey] || 0,
        fullDate: dateKey
      })
      currentDate.setDate(currentDate.getDate() + 1)
    }

    return allDates
  }

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-background pb-bottom-nav">
        <div className="phone-container-padded py-6">
          {/* Header */}
          <div className="mb-6">
            <Link
              to="/analytics"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Pulse
            </Link>
            <h1 className="text-3xl font-bold text-foreground mb-2">Detailed Analytics</h1>
            <p className="text-muted-foreground">Full platform metrics and trends</p>
          </div>

          {/* Timeframe Selector */}
          <div className="mb-6">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Select Timeframe
            </label>
            <Select value={selectedTimeframe} onValueChange={setSelectedTimeframe}>
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder="Select timeframe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="180">Last 180 days</SelectItem>
                <SelectItem value="365">Last 365 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <p className="text-destructive">{error}</p>
              </CardContent>
            </Card>
          )}

          {/* Analytics Data */}
          {!isLoading && !error && analyticsData && (
            <div className="space-y-6">
              {/* Users Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-500" />
                    Users
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Users (Ever)</p>
                      <p className="text-3xl font-bold text-foreground">
                        {analyticsData.users.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New Sign-ups</p>
                      <p className="text-3xl font-bold text-green-600">
                        +{analyticsData.users.newSignUps.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {analyticsData.users.createdAtTimestamps.length > 0 && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-foreground mb-4">Signup Trend</p>
                      <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={processSignupTimestamps(analyticsData.users.createdAtTimestamps)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground) / 0.2)" />
                          <XAxis 
                            dataKey="date" 
                            stroke="hsl(var(--muted-foreground))"
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            stroke="hsl(var(--muted-foreground))"
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'hsl(var(--background))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#3b82f6" 
                            dot={{ fill: '#3b82f6', r: 4 }}
                            activeDot={{ r: 6 }}
                            strokeWidth={2}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Artists Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Palette className="h-5 w-5 text-pink-500" />
                    Artists
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Artists (Ever)</p>
                      <p className="text-3xl font-bold text-foreground">
                        {analyticsData.artists.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New Sign-ups</p>
                      <p className="text-3xl font-bold text-green-600">
                        +{analyticsData.artists.newSignUps.toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {analyticsData.artists.createdAtTimestamps.length > 0 && (
                    <div className="pt-4 border-t">
                      <p className="text-sm font-semibold text-foreground mb-4">Signup Trend</p>
                      <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={processSignupTimestamps(analyticsData.artists.createdAtTimestamps)}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground) / 0.2)" />
                          <XAxis 
                            dataKey="date" 
                            stroke="hsl(var(--muted-foreground))"
                            tick={{ fontSize: 12 }}
                          />
                          <YAxis 
                            stroke="hsl(var(--muted-foreground))"
                            tick={{ fontSize: 12 }}
                          />
                          <Tooltip 
                            contentStyle={{
                              backgroundColor: 'hsl(var(--background))',
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#ec4899" 
                            dot={{ fill: '#ec4899', r: 4 }}
                            activeDot={{ r: 6 }}
                            strokeWidth={2}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Collections Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-purple-500" />
                    Collections
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New in Timeframe</p>
                      <p className="text-2xl font-bold text-green-600">
                        +{analyticsData.collections.newInTimeframe.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Ever</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.collections.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Unique Users</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.collections.uniqueUsers.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Avg per User</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.collections.avgPerUser.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Saves Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Heart className="h-5 w-5 text-red-500" />
                    Saves
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New in Timeframe</p>
                      <p className="text-2xl font-bold text-green-600">
                        +{analyticsData.saves.newInTimeframe.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Ever</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.saves.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Unique Users</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.saves.uniqueUsers.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Avg per User</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.saves.avgPerUser.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Comments Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-orange-500" />
                    Comments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New in Timeframe</p>
                      <p className="text-2xl font-bold text-green-600">
                        +{analyticsData.comments.newInTimeframe.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Ever</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.comments.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Unique Users</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.comments.uniqueUsers.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Follows Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-cyan-500" />
                    Follows
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">New in Timeframe</p>
                      <p className="text-2xl font-bold text-green-600">
                        +{analyticsData.follows.newInTimeframe.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Total Ever</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.follows.totalEver.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Unique Users</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.follows.uniqueUsers.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Avg per User</p>
                      <p className="text-2xl font-bold text-foreground">
                        {analyticsData.follows.avgPerUser.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Refresh Button */}
              <div className="flex justify-center pt-4">
                <Button 
                  onClick={() => fetchAnalytics(parseInt(selectedTimeframe))}
                  disabled={isLoading}
                  variant="outline"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Refresh Data
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminAuthGuard>
  )
}

